import { useState, useEffect, useMemo, useCallback } from 'react';
import { Shop } from '../types/Shop';
import { getShops, refreshShops } from '../services/shopService';
import { filterVisibleShops, filterShopsByTags } from '../utils/shop';

interface UseShopsReturn {
    shops: Shop[];
    loading: boolean;
    filteredShops: Shop[];
    handleShopCreated: (apiResponse: any) => Promise<void>;
    handleShopDeleted: (shopId: string) => Promise<void>;
    handleShopUpdated: () => Promise<void>;
    loadShops: () => Promise<void>;
}

interface UseShopsProps {
    selectedFilters: string[];
    userId?: string;
}

/**
 * Custom hook for managing shop data
 * Handles loading, filtering, CRUD operations, and cache management
 */
export function useShops({ selectedFilters }: UseShopsProps): UseShopsReturn {
    const [shops, setShops] = useState<Shop[]>([]);
    const [loading, setLoading] = useState(true);
    const [now, setNow] = useState(new Date());

    // Update 'now' every minute for filtering expired shops
    useEffect(() => {
        const interval = setInterval(() => {
            setNow(new Date());
        }, 60000); // 1 minute
        return () => clearInterval(interval);
    }, []);

    // Load shops data with caching
    const loadShops = useCallback(async () => {
        try {
            const shopsData = await getShops();
            setShops(shopsData);
        } catch (error) {
            console.error('Failed to load shops:', error);
            // getShops() already handles fallback to mock data
            setShops([]);
        }
    }, []);

    // Initialize shops on mount
    useEffect(() => {
        const initializeShops = async () => {
            await loadShops();
            setLoading(false);
        };

        initializeShops();
    }, [loadShops]);

    // Filter shops using utility functions
    const filteredShops = useMemo(() => {
        const visibleShops = filterVisibleShops(shops, now);
        return filterShopsByTags(visibleShops, selectedFilters);
    }, [shops, selectedFilters, now]);

    // Handle new custom shop creation
    const handleShopCreated = useCallback(async (apiResponse: any) => {
        console.log('✅ [Custom Shop] New shop created:', apiResponse.data || apiResponse);

        // Refresh the entire shop list to ensure we get the most up-to-date data
        try {
            const refreshedShops = await refreshShops(); // Force refresh to bypass cache
            setShops(refreshedShops);
            console.log('✅ [Custom Shop] Shop list refreshed after creation');
        } catch (error) {
            console.error('⚠️ [Custom Shop] Failed to refresh shop list:', error);
            // Fallback: just add the new shop to the current list
            const newShop = apiResponse.data || apiResponse;
            setShops(prevShops => [...prevShops, newShop]);
        }
    }, []);

    // Handler to refresh shops data after deletion
    const handleShopDeleted = useCallback(async (deletedShopId: string) => {
        // Optimistically remove from UI first
        setShops(prevShops => prevShops.filter(shop => shop.id !== deletedShopId));
        console.log('🗑️ [Shops] Shop removed from list (UI only):', deletedShopId);

        // Refresh data to ensure consistency
        try {
            const refreshedShops = await refreshShops();
            setShops(refreshedShops);
            console.log('✅ [Shops] Shop list refreshed after deletion');
        } catch (error) {
            console.error('⚠️ [Shops] Failed to refresh shop list after deletion:', error);
        }
    }, []);

    // Handler to refresh shops data after updates
    const handleShopUpdated = useCallback(async () => {
        try {
            const refreshedShops = await refreshShops();
            setShops(refreshedShops);
            console.log('✅ [Shops] Shop list refreshed after update');
        } catch (error) {
            console.error('⚠️ [Shops] Failed to refresh shop list after update:', error);
        }
    }, []);

    return {
        shops,
        loading,
        filteredShops,
        handleShopCreated,
        handleShopDeleted,
        handleShopUpdated,
        loadShops,
    };
}

/**
 * Hook to get user-specific shops
 */
export function useMyShops(shops: Shop[], userId?: string): Shop[] {
    return useMemo(() => {
        if (!userId) return [];
        return shops.filter(shop => shop.isCustom && shop.createdByUserId === userId);
    }, [shops, userId]);
}
